import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { 
  Zap,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Settings,
  Building2,
  AlertCircle,
  CheckCircle,
  Search,
  Filter,
  Eye,
  EyeOff,
  ToggleLeft,
  ToggleRight,
  Loader,
  Mail,
  MessageSquare,
  BarChart3,
  Database,
  Brain,
  Info
} from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTheme } from '../../../../contexts/ThemeContext';
import { api } from '../../../../utils/api';
import './AutomationManagement.css';

const AutomationManagement = () => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  
  const isSuperAdmin = user?.userType === 'admin' && (user?.isSuperAdmin === true || user?.role === 'SuperAdmin');
  
  const [activeTab, setActiveTab] = useState('endpoints'); // endpoints, tenants, settings
  const [endpoints, setEndpoints] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tenantAssignments, setTenantAssignments] = useState({});
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showEndpointModal, setShowEndpointModal] = useState(false);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [editingEndpoint, setEditingEndpoint] = useState(null);
  const [tenantList, setTenantList] = useState([]);
  const [tenantStats, setTenantStats] = useState({});

  const categoryIcons = {
    'Email': Mail,
    'Messaging': MessageSquare,
    'Reporting': BarChart3,
    'Entity': Database,
    'AI': Brain
  };

  useEffect(() => {
    loadAutomationConfig();
    loadTenantList();
    loadTenantStats();
  }, []);

  const loadAutomationConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/automation/config');
      
      if (response.success) {
        setEndpoints(response.config.endpoints || []);
        setCategories(response.config.categories || []);
        setTenantAssignments(response.config.tenantAssignments || {});
        setSettings(response.config.settings || {});
      }
    } catch (error) {
      console.error('Error loading automation config:', error);
      toast.error('Failed to load automation configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadTenantList = async () => {
    try {
      const response = await api.get('/tenants');
      if (response.tenants) {
        // Map the response to the format we need
        const mappedTenants = response.tenants.map(tenant => ({
          tenantCode: tenant.code,
          code: tenant.code,
          name: tenant.name || tenant.code,
          companyName: tenant.companyName
        }));
        setTenantList(mappedTenants);
      }
    } catch (error) {
      console.error('Error loading tenants:', error);
      // If tenants endpoint fails, use fallback from config
      const tenantsFromConfig = Object.keys(tenantAssignments).map(code => ({
        tenantCode: code,
        code: code,
        name: code
      }));
      setTenantList(tenantsFromConfig);
    }
  };

  const loadTenantStats = async () => {
    try {
      console.log('Loading tenant automation statistics...');
      const response = await api.get('/admin/automation/tenant-stats');
      console.log('Tenant stats response:', response);
      
      if (response.success && response.stats) {
        setTenantStats(response.stats);
        console.log('Tenant stats loaded successfully:', Object.keys(response.stats).length, 'tenants');
      } else {
        console.warn('Tenant stats response missing data:', response);
      }
    } catch (error) {
      console.error('Error loading tenant statistics:', error);
      console.error('Error details:', error.response?.data || error.message);
      // Fail silently - stats are optional, but log for debugging
    }
  };

  const handleSaveEndpoint = async (endpointData) => {
    try {
      setSaving(true);
      
      let response;
      if (editingEndpoint) {
        // Update existing endpoint
        response = await api.put(`/admin/automation/endpoints/${editingEndpoint.id}`, endpointData);
      } else {
        // Create new endpoint
        response = await api.post('/admin/automation/endpoints', endpointData);
      }
      
      if (response.success) {
        toast.success(editingEndpoint ? 'Endpoint updated successfully' : 'Endpoint created successfully');
        loadAutomationConfig();
        setShowEndpointModal(false);
        setEditingEndpoint(null);
      }
    } catch (error) {
      console.error('Error saving endpoint:', error);
      toast.error(error.message || 'Failed to save endpoint');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEndpoint = async (endpointId) => {
    if (!window.confirm('Are you sure you want to delete this endpoint? It will be removed from all tenant assignments.')) {
      return;
    }
    
    try {
      const response = await api.delete(`/admin/automation/endpoints/${endpointId}`);
      
      if (response.success) {
        toast.success('Endpoint deleted successfully');
        loadAutomationConfig();
      }
    } catch (error) {
      console.error('Error deleting endpoint:', error);
      toast.error('Failed to delete endpoint');
    }
  };

  const handleToggleEndpointForTenant = async (tenantCode, endpointId, enable) => {
    try {
      const endpoint = enable ? 'enable-endpoint' : 'disable-endpoint';
      const response = await api.post(`/admin/automation/tenants/${tenantCode}/${endpoint}`, {
        endpointId
      });
      
      if (response.success) {
        toast.success(`Endpoint ${enable ? 'enabled' : 'disabled'} for tenant`);
        loadAutomationConfig();
      }
    } catch (error) {
      console.error('Error toggling endpoint:', error);
      toast.error('Failed to update endpoint status');
    }
  };

  const handleUpdateTenantAssignment = async (tenantCode, assignmentData) => {
    try {
      setSaving(true);
      const response = await api.put(`/admin/automation/tenants/${tenantCode}`, assignmentData);
      
      if (response.success) {
        toast.success('Tenant assignment updated successfully');
        loadAutomationConfig();
        setShowTenantModal(false);
        setSelectedTenant(null);
      }
    } catch (error) {
      console.error('Error updating tenant assignment:', error);
      toast.error('Failed to update tenant assignment');
    } finally {
      setSaving(false);
    }
  };

  const filteredEndpoints = endpoints.filter(endpoint => {
    const matchesSearch = endpoint.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         endpoint.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         endpoint.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || endpoint.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="automation-loading">
        <Loader className="spin" size={48} />
        <p>Loading automation configuration...</p>
      </div>
    );
  }

  if (!isSuperAdmin && activeTab === 'endpoints') {
    return (
      <div className="automation-access-denied">
        <AlertCircle size={48} />
        <h2>Access Denied</h2>
        <p>Only Super Admins can manage automation endpoints</p>
      </div>
    );
  }

  return (
    <div className="automation-management">
      <div className="automation-header">
        <div className="header-title">
          <Zap size={32} />
          <div>
            <h1>Automation Management</h1>
            <p>Manage automation endpoints and tenant assignments</p>
          </div>
        </div>
        
        {isSuperAdmin && activeTab === 'endpoints' && (
          <button 
            className="btn-primary"
            onClick={() => {
              setEditingEndpoint(null);
              setShowEndpointModal(true);
            }}
          >
            <Plus size={20} />
            Register Endpoint
          </button>
        )}
      </div>

      <div className="automation-tabs">
        <button 
          className={`tab ${activeTab === 'endpoints' ? 'active' : ''}`}
          onClick={() => setActiveTab('endpoints')}
        >
          <Database size={18} />
          Endpoints
        </button>
        <button 
          className={`tab ${activeTab === 'tenants' ? 'active' : ''}`}
          onClick={() => setActiveTab('tenants')}
        >
          <Building2 size={18} />
          Tenant Assignments
        </button>
        <button 
          className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <Settings size={18} />
          Settings
        </button>
      </div>

      <div className="automation-content">
        {activeTab === 'endpoints' && (
          <EndpointsTab
            endpoints={filteredEndpoints}
            categories={categories}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            onEdit={(endpoint) => {
              setEditingEndpoint(endpoint);
              setShowEndpointModal(true);
            }}
            onDelete={handleDeleteEndpoint}
            categoryIcons={categoryIcons}
            isSuperAdmin={isSuperAdmin}
          />
        )}

        {activeTab === 'tenants' && (
          <TenantAssignmentsTab
            tenantAssignments={tenantAssignments}
            endpoints={endpoints}
            tenantList={tenantList}
            tenantStats={tenantStats}
            onToggleEndpoint={handleToggleEndpointForTenant}
            onEditTenant={(tenantCode) => {
              setSelectedTenant(tenantCode);
              setShowTenantModal(true);
            }}
            categoryIcons={categoryIcons}
            isSuperAdmin={isSuperAdmin}
          />
        )}

        {activeTab === 'settings' && (
          <SettingsTab
            settings={settings}
            onSave={async (newSettings) => {
              try {
                setSaving(true);
                const response = await api.put('/admin/automation/settings', { settings: newSettings });
                if (response.success) {
                  toast.success('Settings updated successfully');
                  setSettings(newSettings);
                }
              } catch (error) {
                toast.error('Failed to update settings');
              } finally {
                setSaving(false);
              }
            }}
            isSuperAdmin={isSuperAdmin}
            saving={saving}
          />
        )}
      </div>

      {showEndpointModal && (
        <EndpointModal
          endpoint={editingEndpoint}
          categories={categories}
          onSave={handleSaveEndpoint}
          onClose={() => {
            setShowEndpointModal(false);
            setEditingEndpoint(null);
          }}
          saving={saving}
        />
      )}

      {showTenantModal && selectedTenant && (
        <TenantAssignmentModal
          tenantCode={selectedTenant}
          assignment={tenantAssignments[selectedTenant] || { enabled: false, allowedEndpoints: [], disabledEndpoints: [] }}
          endpoints={endpoints}
          categories={categories}
          onSave={handleUpdateTenantAssignment}
          onClose={() => {
            setShowTenantModal(false);
            setSelectedTenant(null);
          }}
          saving={saving}
          categoryIcons={categoryIcons}
        />
      )}
    </div>
  );
};

// Endpoints Tab Component
const EndpointsTab = ({ 
  endpoints, 
  categories, 
  searchTerm, 
  setSearchTerm, 
  selectedCategory, 
  setSelectedCategory,
  onEdit,
  onDelete,
  categoryIcons,
  isSuperAdmin
}) => {
  return (
    <div className="endpoints-tab">
      <div className="endpoints-filters">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search endpoints..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="category-filter">
          <Filter size={18} />
          <select 
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="endpoints-grid">
        {endpoints.map(endpoint => {
          const CategoryIcon = categoryIcons[endpoint.category] || Database;
          return (
            <div key={endpoint.id} className={`endpoint-card ${!endpoint.enabled ? 'disabled' : ''}`}>
              <div className="endpoint-header">
                <div className="endpoint-icon">
                  <CategoryIcon size={24} />
                </div>
                <div className="endpoint-title">
                  <h3>{endpoint.name}</h3>
                  <span className="endpoint-category">{endpoint.category}</span>
                </div>
                <div className="endpoint-status">
                  {endpoint.enabled ? (
                    <CheckCircle size={18} className="status-enabled" />
                  ) : (
                    <AlertCircle size={18} className="status-disabled" />
                  )}
                </div>
              </div>
              
              <div className="endpoint-body">
                <p className="endpoint-description">{endpoint.description}</p>
                <div className="endpoint-id">
                  <code>{endpoint.id}</code>
                </div>
                
                {endpoint.parameters && endpoint.parameters.length > 0 && (
                  <div className="endpoint-parameters">
                    <h4>Parameters:</h4>
                    <ul>
                      {endpoint.parameters.slice(0, 3).map((param, idx) => (
                        <li key={idx}>
                          <strong>{param.name}</strong>
                          {param.required && <span className="required">*</span>}
                          <span className="param-type">({param.type})</span>
                        </li>
                      ))}
                      {endpoint.parameters.length > 3 && (
                        <li className="param-more">+{endpoint.parameters.length - 3} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
              
              {isSuperAdmin && (
                <div className="endpoint-actions">
                  <button 
                    className="btn-icon"
                    onClick={() => onEdit(endpoint)}
                    title="Edit endpoint"
                  >
                    <Edit size={16} />
                  </button>
                  <button 
                    className="btn-icon btn-danger"
                    onClick={() => onDelete(endpoint.id)}
                    title="Delete endpoint"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {endpoints.length === 0 && (
        <div className="empty-state">
          <Zap size={48} />
          <h3>No endpoints found</h3>
          <p>Start by registering your first automation endpoint</p>
        </div>
      )}
    </div>
  );
};

// Tenant Assignments Tab Component
const TenantAssignmentsTab = ({ 
  tenantAssignments, 
  endpoints, 
  tenantList,
  tenantStats,
  onToggleEndpoint,
  onEditTenant,
  categoryIcons,
  isSuperAdmin
}) => {
  // Use tenantList to show all tenants, not just those in tenantAssignments
  const allTenants = tenantList.length > 0 ? tenantList : Object.keys(tenantAssignments).map(code => ({ tenantCode: code, name: code }));
  const [selectedTenant, setSelectedTenant] = useState(allTenants[0]?.tenantCode || null);

  // Get assignment for selected tenant, or create default if doesn't exist
  const currentTenantAssignment = selectedTenant 
    ? (tenantAssignments[selectedTenant] || {
        enabled: false,
        allowedEndpoints: [],
        disabledEndpoints: [],
        customSettings: {}
      })
    : null;

  return (
    <div className="tenant-assignments-tab">
      <div className="tenant-sidebar">
        <h3>Tenants ({allTenants.length})</h3>
        <div className="tenant-list">
          {allTenants.map(tenant => {
            const tenantCode = tenant.tenantCode || tenant.code;
            const assignment = tenantAssignments[tenantCode];
            const isConfigured = !!assignment;
            
            return (
              <button
                key={tenantCode}
                className={`tenant-item ${selectedTenant === tenantCode ? 'active' : ''}`}
                onClick={() => setSelectedTenant(tenantCode)}
              >
                <Building2 size={16} />
                <span>{tenant.name || tenantCode}</span>
                {isConfigured && assignment.enabled ? (
                  <CheckCircle size={14} className="status-enabled" />
                ) : isConfigured && !assignment.enabled ? (
                  <AlertCircle size={14} className="status-disabled" />
                ) : (
                  <Info size={14} className="status-not-configured" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="tenant-content">
        {selectedTenant && currentTenantAssignment ? (
          <>
            <div className="tenant-header">
              <div>
                <h2>{allTenants.find(t => (t.tenantCode || t.code) === selectedTenant)?.name || selectedTenant}</h2>
                <p>
                  {tenantAssignments[selectedTenant] 
                    ? (currentTenantAssignment.enabled ? 'Automation Enabled' : 'Automation Disabled')
                    : 'Not Configured - Click Configure to enable automation'}
                </p>
              </div>
              {isSuperAdmin && (
                <button 
                  className="btn-secondary"
                  onClick={() => onEditTenant(selectedTenant)}
                >
                  <Settings size={18} />
                  {tenantAssignments[selectedTenant] ? 'Configure' : 'Enable & Configure'}
                </button>
              )}
            </div>

            {tenantStats[selectedTenant] && (
              <div className="tenant-summary">
                <div className="summary-card">
                  <div className="summary-icon">
                    <BarChart3 size={20} />
                  </div>
                  <div className="summary-content">
                    <h4>Total Executions</h4>
                    <p>{tenantStats[selectedTenant].totalExecutions || 0}</p>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon">
                    <Database size={20} />
                  </div>
                  <div className="summary-content">
                    <h4>Automation Jobs</h4>
                    <p>
                      {Object.values(tenantStats[selectedTenant].endpoints || {}).reduce((acc, e) => acc + (e.totalJobs || 0), 0)}
                    </p>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon success">
                    <CheckCircle size={20} />
                  </div>
                  <div className="summary-content">
                    <h4>Active Jobs</h4>
                    <p>
                      {Object.values(tenantStats[selectedTenant].endpoints || {}).reduce((acc, e) => acc + (e.activeJobs || 0), 0)}
                    </p>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon">
                    <Zap size={20} />
                  </div>
                  <div className="summary-content">
                    <h4>Configured Endpoints</h4>
                    <p>{Object.keys(tenantStats[selectedTenant].endpoints || {}).length}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="tenant-endpoints">
              <h3>Assigned Endpoints ({currentTenantAssignment.allowedEndpoints?.length || 0}/{endpoints.length})</h3>
              {!tenantAssignments[selectedTenant] && (
                <div className="info-banner">
                  <Info size={16} />
                  <p>This tenant has not been configured for automation yet. Click "Enable & Configure" to set up automation endpoints.</p>
                </div>
              )}
              <div className="endpoints-list">
                {endpoints.map(endpoint => {
                  const isAllowed = currentTenantAssignment.allowedEndpoints?.includes(endpoint.id) || false;
                  const isDisabled = currentTenantAssignment.disabledEndpoints?.includes(endpoint.id) || false;
                  const CategoryIcon = categoryIcons[endpoint.category] || Database;
                  
                  // Get statistics for this endpoint and tenant
                  const stats = tenantStats[selectedTenant]?.endpoints?.[endpoint.id];
                  
                  return (
                    <div key={endpoint.id} className={`endpoint-row ${isAllowed ? 'enabled' : 'disabled'}`}>
                      <div className="endpoint-info">
                        <CategoryIcon size={20} />
                        <div className="endpoint-main-info">
                          <h4>{endpoint.name}</h4>
                          <p>{endpoint.description}</p>
                          {stats && (
                            <div className="endpoint-stats">
                              <span className="stat-item">
                                <strong>{stats.totalJobs || 0}</strong> jobs
                              </span>
                              <span className="stat-item stat-active">
                                <strong>{stats.activeJobs || 0}</strong> active
                              </span>
                              {stats.successJobs > 0 && (
                                <span className="stat-item stat-success">
                                  <strong>{stats.successJobs}</strong> success
                                </span>
                              )}
                              {stats.failedJobs > 0 && (
                                <span className="stat-item stat-error">
                                  <strong>{stats.failedJobs}</strong> failed
                                </span>
                              )}
                              {stats.lastExecutedAt && (
                                <span className="stat-item stat-time">
                                  Last: {new Date(stats.lastExecutedAt).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      {isSuperAdmin && (
                        <button
                          className={`toggle-btn ${isAllowed ? 'enabled' : ''}`}
                          onClick={() => onToggleEndpoint(selectedTenant, endpoint.id, !isAllowed)}
                          title={isAllowed ? 'Disable endpoint' : 'Enable endpoint'}
                        >
                          {isAllowed ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <div className="empty-state">
            <Building2 size={48} />
            <h3>No tenant selected</h3>
            <p>Select a tenant from the sidebar to view assignments</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Settings Tab Component
const SettingsTab = ({ settings, onSave, isSuperAdmin, saving }) => {
  const [localSettings, setLocalSettings] = useState(settings);

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const handleChange = (key, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  return (
    <div className="settings-tab">
      <div className="settings-form">
        <div className="form-section">
          <h3>General Settings</h3>
          
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={localSettings.allowTenantRegistration || false}
                onChange={(e) => handleChange('allowTenantRegistration', e.target.checked)}
                disabled={!isSuperAdmin}
              />
              Allow Tenant Registration
            </label>
            <p className="help-text">If enabled, tenants can register their own endpoints</p>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={localSettings.requireSuperAdminApproval || false}
                onChange={(e) => handleChange('requireSuperAdminApproval', e.target.checked)}
                disabled={!isSuperAdmin}
              />
              Require Super Admin Approval
            </label>
            <p className="help-text">New endpoint registrations require super admin approval</p>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={localSettings.enableEndpointLogging || false}
                onChange={(e) => handleChange('enableEndpointLogging', e.target.checked)}
                disabled={!isSuperAdmin}
              />
              Enable Endpoint Logging
            </label>
            <p className="help-text">Log all endpoint executions for auditing</p>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={localSettings.enableRateLimiting || false}
                onChange={(e) => handleChange('enableRateLimiting', e.target.checked)}
                disabled={!isSuperAdmin}
              />
              Enable Rate Limiting
            </label>
            <p className="help-text">Apply rate limits to endpoint executions</p>
          </div>
        </div>

        {localSettings.enableRateLimiting && (
          <div className="form-section">
            <h3>Rate Limiting</h3>
            
            <div className="form-group">
              <label>Max Requests</label>
              <input
                type="number"
                value={localSettings.defaultRateLimit?.maxRequests || 100}
                onChange={(e) => handleChange('defaultRateLimit', {
                  ...localSettings.defaultRateLimit,
                  maxRequests: parseInt(e.target.value)
                })}
                disabled={!isSuperAdmin}
              />
              <p className="help-text">Maximum number of requests allowed</p>
            </div>

            <div className="form-group">
              <label>Time Window (ms)</label>
              <input
                type="number"
                value={localSettings.defaultRateLimit?.windowMs || 900000}
                onChange={(e) => handleChange('defaultRateLimit', {
                  ...localSettings.defaultRateLimit,
                  windowMs: parseInt(e.target.value)
                })}
                disabled={!isSuperAdmin}
              />
              <p className="help-text">Time window in milliseconds (900000 = 15 minutes)</p>
            </div>
          </div>
        )}

        {isSuperAdmin && (
          <div className="form-actions">
            <button 
              className="btn-primary"
              onClick={() => onSave(localSettings)}
              disabled={saving}
            >
              {saving ? <Loader className="spin" size={18} /> : <Save size={18} />}
              Save Settings
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Endpoint Modal Component
const EndpointModal = ({ endpoint, categories, onSave, onClose, saving }) => {
  const [formData, setFormData] = useState(endpoint || {
    id: '',
    name: '',
    description: '',
    category: categories[0]?.id || '',
    enabled: true,
    parameters: []
  });

  const [newParameter, setNewParameter] = useState({
    name: '',
    type: 'string',
    required: false,
    description: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddParameter = () => {
    if (!newParameter.name) {
      toast.error('Parameter name is required');
      return;
    }

    setFormData(prev => ({
      ...prev,
      parameters: [...(prev.parameters || []), newParameter]
    }));

    setNewParameter({
      name: '',
      type: 'string',
      required: false,
      description: ''
    });
  };

  const handleRemoveParameter = (index) => {
    setFormData(prev => ({
      ...prev,
      parameters: prev.parameters.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.id || !formData.name || !formData.description || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{endpoint ? 'Edit Endpoint' : 'Register New Endpoint'}</h2>
          <button className="btn-icon" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label>Endpoint ID *</label>
            <input
              type="text"
              value={formData.id}
              onChange={(e) => handleChange('id', e.target.value)}
              placeholder="e.g., send_email"
              disabled={!!endpoint}
              required
            />
            <p className="help-text">Unique identifier for the endpoint (snake_case)</p>
          </div>

          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., Send Email"
              required
            />
          </div>

          <div className="form-group">
            <label>Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Describe what this endpoint does..."
              rows={3}
              required
            />
          </div>

          <div className="form-group">
            <label>Category *</label>
            <select
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              required
            >
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) => handleChange('enabled', e.target.checked)}
              />
              Enabled
            </label>
          </div>

          <div className="form-section">
            <h3>Parameters</h3>
            
            {formData.parameters && formData.parameters.length > 0 && (
              <div className="parameters-list">
                {formData.parameters.map((param, index) => (
                  <div key={index} className="parameter-item">
                    <div className="parameter-info">
                      <strong>{param.name}</strong>
                      {param.required && <span className="required">*</span>}
                      <span className="param-type">({param.type})</span>
                      <p>{param.description}</p>
                    </div>
                    <button
                      type="button"
                      className="btn-icon btn-danger"
                      onClick={() => handleRemoveParameter(index)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="add-parameter-form">
              <input
                type="text"
                placeholder="Parameter name"
                value={newParameter.name}
                onChange={(e) => setNewParameter(prev => ({ ...prev, name: e.target.value }))}
              />
              <select
                value={newParameter.type}
                onChange={(e) => setNewParameter(prev => ({ ...prev, type: e.target.value }))}
              >
                <option value="string">String</option>
                <option value="number">Number</option>
                <option value="boolean">Boolean</option>
                <option value="array">Array</option>
                <option value="object">Object</option>
              </select>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={newParameter.required}
                  onChange={(e) => setNewParameter(prev => ({ ...prev, required: e.target.checked }))}
                />
                Required
              </label>
              <button
                type="button"
                className="btn-secondary"
                onClick={handleAddParameter}
              >
                <Plus size={16} />
                Add
              </button>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? <Loader className="spin" size={18} /> : <Save size={18} />}
              {endpoint ? 'Update' : 'Create'} Endpoint
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Tenant Assignment Modal Component
const TenantAssignmentModal = ({ 
  tenantCode, 
  assignment, 
  endpoints, 
  categories,
  onSave, 
  onClose, 
  saving,
  categoryIcons 
}) => {
  const [formData, setFormData] = useState({
    enabled: assignment?.enabled || false,
    allowedEndpoints: [...(assignment?.allowedEndpoints || [])],
    disabledEndpoints: [...(assignment?.disabledEndpoints || [])],
    customSettings: assignment?.customSettings || {}
  });

  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredEndpoints = selectedCategory === 'all' 
    ? endpoints 
    : endpoints.filter(e => e.category === selectedCategory);

  const toggleEndpoint = (endpointId) => {
    setFormData(prev => {
      const isAllowed = prev.allowedEndpoints.includes(endpointId);
      
      if (isAllowed) {
        return {
          ...prev,
          allowedEndpoints: prev.allowedEndpoints.filter(id => id !== endpointId),
          disabledEndpoints: [...prev.disabledEndpoints, endpointId]
        };
      } else {
        return {
          ...prev,
          allowedEndpoints: [...prev.allowedEndpoints, endpointId],
          disabledEndpoints: prev.disabledEndpoints.filter(id => id !== endpointId)
        };
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(tenantCode, formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Configure Automation for {tenantCode}</h2>
          <button className="btn-icon" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) => setFormData(prev => ({ ...prev, enabled: e.target.checked }))}
              />
              Enable Automation for this Tenant
            </label>
          </div>

          <div className="form-section">
            <div className="section-header">
              <h3>Endpoint Access</h3>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="category-filter-small"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div className="endpoints-checklist">
              {filteredEndpoints.map(endpoint => {
                const isAllowed = formData.allowedEndpoints.includes(endpoint.id);
                const CategoryIcon = categoryIcons[endpoint.category] || Database;
                
                return (
                  <div key={endpoint.id} className="checkbox-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={isAllowed}
                        onChange={() => toggleEndpoint(endpoint.id)}
                      />
                      <CategoryIcon size={16} />
                      <div className="checkbox-label-content">
                        <strong>{endpoint.name}</strong>
                        <span>{endpoint.description}</span>
                      </div>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? <Loader className="spin" size={18} /> : <Save size={18} />}
              Save Assignment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AutomationManagement;
